import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Calendar as CalendarIcon, Clock, Users, Plus, Minus } from 'lucide-react';
import axios from 'axios';
import { Button } from './ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { packages, addOns, pricingMultipliers } from '../mock';
import { useToast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const QuoteBuilder = ({ onClose }) => {
  const { toast } = useToast();
  const [selectedPackage, setSelectedPackage] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [eventTime, setEventTime] = useState('');
  const [guestCount, setGuestCount] = useState('');
  const [eventType, setEventType] = useState('');
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [extraHours, setExtraHours] = useState(0);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [specialRequests, setSpecialRequests] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Calculate price directly - no useEffect needed
  let totalPrice = 0;
  if (selectedPackage) {
    const pkg = packages.find(p => p.id === selectedPackage);
    if (pkg && !pkg.isPOA) {
      totalPrice = pkg.basePrice;
      
      if (extraHours > 0) {
        const extraHourAddon = addOns.find(a => a.id === 'extra-hour');
        if (extraHourAddon) totalPrice += extraHourAddon.price * extraHours;
      }

      selectedAddOns.forEach(addonId => {
        const addon = addOns.find(a => a.id === addonId);
        if (addon && addon.id !== 'extra-hour') totalPrice += addon.price;
      });
    }
  }

  const toggleAddOn = (addonId) => {
    if (selectedAddOns.includes(addonId)) {
      setSelectedAddOns(selectedAddOns.filter(id => id !== addonId));
    } else {
      setSelectedAddOns([...selectedAddOns, addonId]);
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    
    if (!selectedPackage || !eventDate || !eventTime || !customerName || !customerEmail || !customerPhone) {
      toast({
        title: "Missing Information",
        description: "Please fill in all required fields (Package, Date, Time, Name, Email, Phone)",
        variant: "destructive",
        duration: 5000,
      });
      return;
    }

    const quoteData = {
      package: packages.find(p => p.id === selectedPackage)?.name,
      eventDate,
      eventTime,
      eventType,
      guestCount,
      addOns: selectedAddOns.map(id => addOns.find(a => a.id === id)?.name).filter(Boolean),
      extraHours,
      totalPrice,
      customerName,
      customerEmail,
      customerPhone,
      specialRequests
    };

    setIsSubmitting(true);

    try {
      // Submit to backend
      const response = await axios.post(`${API}/quotes`, quoteData);
      
      console.log('Quote submitted successfully:', response.data);
      
      // Reset submitting state
      setIsSubmitting(false);
      
      // Show success state
      setSubmitSuccess(true);
      
      toast({
        title: "✅ Quote Submitted Successfully!",
        description: `We've received your quote request for £${totalPrice}. We'll contact you at ${customerEmail} within 24 hours.`,
        duration: 8000,
      });

      // Close modal after showing success
      setTimeout(() => {
        onClose && onClose();
      }, 5000);
      
    } catch (error) {
      console.error('Error submitting quote:', error);
      console.error('Error details:', error.response?.data || error.message);
      setIsSubmitting(false);
      
      let errorMessage = "There was an issue submitting your quote. Please try again or contact us directly.";
      if (error.response?.status === 422) {
        errorMessage = "Please check all fields are filled correctly.";
      } else if (error.code === 'ERR_NETWORK') {
        errorMessage = "Network error. Please check your internet connection.";
      }
      
      toast({
        title: "Submission Error",
        description: errorMessage,
        variant: "destructive",
        duration: 8000,
      });
    }
  };

  // Show success message
  if (submitSuccess) {
    return (
      <div className="text-center py-12">
        <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-emerald-700" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M5 13l4 4L19 7"></path>
          </svg>
        </div>
        <h2 className="text-3xl font-bold text-black mb-4">Quote Submitted Successfully!</h2>
        <p className="text-lg text-gray-600 mb-2">Thank you, {customerName}!</p>
        <p className="text-gray-600 mb-6">We've received your quote request for <span className="font-bold text-emerald-700">£{totalPrice}</span></p>
        <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 max-w-md mx-auto">
          <p className="text-sm text-gray-700 mb-2">
            <strong>What happens next?</strong>
          </p>
          <ul className="text-sm text-gray-700 text-left space-y-2">
            <li className="flex items-start gap-2">
              <span className="text-emerald-700 mt-0.5">✓</span>
              <span>We'll review your quote for {eventDate}</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-700 mt-0.5">✓</span>
              <span>We'll contact you at {customerEmail} within 24 hours</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-emerald-700 mt-0.5">✓</span>
              <span>We'll answer any questions and confirm availability</span>
            </li>
          </ul>
        </div>
        <p className="text-sm text-gray-500 mt-6">This window will close in 5 seconds...</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Package Selection */}
      <div>
        <Label className="text-lg font-semibold mb-4 block">Select Your Package *</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {packages.map((pkg) => (
            <Card
              key={pkg.id}
              className={`cursor-pointer transition-all ${selectedPackage === pkg.id ? 'border-emerald-700 border-2 shadow-lg' : 'border-gray-200 hover:border-gray-300'}`}
              onClick={() => setSelectedPackage(pkg.id)}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{pkg.name}</span>
                  <span className="text-emerald-700">{pkg.isPOA ? 'POA' : `£${pkg.basePrice}`}</span>
                </CardTitle>
                <CardDescription>{pkg.isPOA ? 'Contact us for pricing' : `${pkg.duration} hours coverage`}</CardDescription>
              </CardHeader>
            </Card>
          ))}
        </div>
      </div>

      {/* Event Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="eventDate" className="mb-2 block">Event Date *</Label>
          <Input
            id="eventDate"
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            className="w-full"
          />
        </div>
        <div>
          <Label htmlFor="eventTime" className="mb-2 block">Event Time *</Label>
          <Input
            id="eventTime"
            type="time"
            value={eventTime}
            onChange={(e) => setEventTime(e.target.value)}
            className="w-full"
          />
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <Label htmlFor="eventType" className="mb-2 block">Event Type</Label>
          <Select value={eventType} onValueChange={setEventType}>
            <SelectTrigger>
              <SelectValue placeholder="Select event type" />
            </SelectTrigger>
            <SelectContent className="z-[100]">
              <SelectItem value="wedding">Wedding</SelectItem>
              <SelectItem value="birthday">Birthday Party</SelectItem>
              <SelectItem value="corporate">Corporate Event</SelectItem>
              <SelectItem value="anniversary">Anniversary</SelectItem>
              <SelectItem value="other">Other</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="guestCount" className="mb-2 block">Estimated Guest Count</Label>
          <Input
            id="guestCount"
            type="number"
            placeholder="e.g., 100"
            value={guestCount}
            onChange={(e) => setGuestCount(e.target.value)}
            className="w-full"
          />
        </div>
      </div>

      {/* Extra Hours */}
      <div>
        <Label className="text-lg font-semibold mb-4 block">Additional Hours</Label>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold">Extra Hours</p>
                <p className="text-sm text-gray-600">£80 per hour</p>
              </div>
              <div className="flex items-center gap-4">
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setExtraHours(Math.max(0, extraHours - 1))}
                  disabled={extraHours === 0}
                >
                  <Minus className="h-4 w-4" />
                </Button>
                <span className="text-xl font-bold w-8 text-center">{extraHours}</span>
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  onClick={() => setExtraHours(extraHours + 1)}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add-ons */}
      <div>
        <Label className="text-lg font-semibold mb-4 block">Enhance Your Experience</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {addOns.filter(addon => addon.id !== 'extra-hour').map((addon) => (
            <Card key={addon.id} className="cursor-pointer hover:shadow-md transition-shadow">
              <CardContent className="pt-6" onClick={() => toggleAddOn(addon.id)}>
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 mt-0.5 ${selectedAddOns.includes(addon.id) ? 'bg-emerald-700 border-emerald-700' : 'border-gray-300'}`}>
                    {selectedAddOns.includes(addon.id) && (
                      <svg className="w-3 h-3 text-white" fill="none" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" stroke="currentColor">
                        <path d="M5 13l4 4L19 7"></path>
                      </svg>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold">{addon.name}</p>
                    <p className="text-sm text-gray-600">{addon.description}</p>
                    <p className="text-emerald-700 font-semibold mt-2">+£{addon.price}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Customer Details */}
      <div>
        <Label className="text-lg font-semibold mb-4 block">Your Details</Label>
        <div className="space-y-4">
          <div>
            <Label htmlFor="customerName" className="mb-2 block">Full Name *</Label>
            <Input
              id="customerName"
              type="text"
              placeholder="John Smith"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customerEmail" className="mb-2 block">Email *</Label>
              <Input
                id="customerEmail"
                type="email"
                placeholder="john@example.com"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="customerPhone" className="mb-2 block">Phone Number *</Label>
              <Input
                id="customerPhone"
                type="tel"
                placeholder="07XXX XXXXXX"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="specialRequests" className="mb-2 block">Special Requests or Questions</Label>
            <Textarea
              id="specialRequests"
              placeholder="Any specific requirements, themes, or questions?"
              value={specialRequests}
              onChange={(e) => setSpecialRequests(e.target.value)}
              rows={4}
            />
          </div>
        </div>
      </div>

      {/* Price Summary */}
      {selectedPackage && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-2xl">Your Quote Summary</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between text-lg">
              <span>Base Package:</span>
              <span className="font-semibold">£{packages.find(p => p.id === selectedPackage)?.basePrice}</span>
            </div>
            {extraHours > 0 && (
              <div className="flex justify-between">
                <span>Extra Hours ({extraHours}):</span>
                <span className="font-semibold">£{addOns.find(a => a.id === 'extra-hour')?.price * extraHours}</span>
              </div>
            )}
            {selectedAddOns.filter(id => id !== 'extra-hour').map(addonId => {
              const addon = addOns.find(a => a.id === addonId);
              return (
                <div key={addonId} className="flex justify-between">
                  <span>{addon?.name}:</span>
                  <span className="font-semibold">£{addon?.price}</span>
                </div>
              );
            })}
            <div className="border-t-2 border-emerald-300 pt-3 mt-3">
              <div className="flex justify-between text-2xl font-bold">
                <span>Total:</span>
                <span className="text-emerald-700">{totalPrice > 0 ? `£${totalPrice}` : 'POA'}</span>
              </div>
              <div className="mt-3 space-y-1">
                <p className="text-xs text-gray-600">* Discounts available for weekday bookings</p>
                <p className="text-xs text-gray-600">* Discounts available for charity events</p>
                <p className="text-xs text-gray-600">* Contact us for specific booking requirements</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Terms & Conditions Summary */}
      <Card className="bg-gray-50 border-gray-200">
        <CardContent className="pt-6">
          <h3 className="font-semibold text-gray-900 mb-3">Photo Booth Hire – Key Terms Summary</h3>
          <ul className="space-y-2 text-sm text-gray-700">
            <li>• Bookings are confirmed by email and invoice.</li>
            <li>• Payment of the deposit confirms acceptance of our full Terms & Conditions.</li>
            <li>• Deposits are non-refundable.</li>
            <li>• Cancellations within 14 days of the event are charged in full.</li>
            <li>• The Client must ensure correct event details, suitable space, power, lighting and (where required) strong Wi-Fi.</li>
            <li>• We are not responsible for reduced performance caused by venue lighting or internet issues.</li>
            <li>• If we cancel, a full refund will be provided.</li>
            <li>• Images may be processed using third-party software, including servers outside the UK.</li>
          </ul>
          <p className="text-sm text-gray-600 mt-4">
            Full Terms & Conditions available at:{' '}
            <Link 
              to="/terms" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-emerald-700 hover:text-emerald-800 underline font-medium"
            >
              View Terms & Conditions
            </Link>
          </p>
          <p className="text-xs text-gray-500 mt-3 italic">
            By submitting this quote request, you acknowledge that you have read and agree to be bound by our Terms & Conditions.
          </p>
        </CardContent>
      </Card>

      {/* Submit Button */}
      <Button 
        type="button"
        onClick={handleSubmit}
        size="lg" 
        disabled={isSubmitting}
        className="w-full bg-emerald-700 hover:bg-emerald-800 text-white text-lg py-6 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <>
            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Submitting...
          </>
        ) : 'Request Quote'}
      </Button>
    </div>
  );
};

export default QuoteBuilder;